
import { useState, useEffect } from "react";
import { useMapInit } from "./maps/useMapInit";
import { useMapEvents } from "./maps/useMapEvents";
import { createMapMarker, getReverseGeocode } from "@/utils/mapUtils";
import type { Location } from "@/components/maps/types";

interface UseGoogleMapProps {
  mapRef: React.RefObject<HTMLDivElement>;
  initialLocation?: Location | null;
  onLocationSelected: (location: Location) => void;
}

export const useGoogleMap = ({ 
  mapRef, 
  initialLocation, 
  onLocationSelected 
}: UseGoogleMapProps) => {
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [marker, setMarker] = useState<google.maps.marker.AdvancedMarkerElement | null>(null);
  const { isGoogleMapsLoaded, mapError } = useMapInit();
  
  // Initialize map and marker
  useEffect(() => {
    if (!isGoogleMapsLoaded || !mapRef.current || mapError) return;

    try {
      console.log("Initializing Google Map with ref:", mapRef.current);
      
      const defaultLocation = { lat: 51.5074, lng: -0.1278 };
      const initialPos = initialLocation || defaultLocation;
      const initialLatLng = new google.maps.LatLng(initialPos.lat, initialPos.lng);

      const newMap = new google.maps.Map(mapRef.current, {
        center: initialLatLng,
        zoom: 14,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
        disableDefaultUI: false,
        clickableIcons: false,
      });
      
      const newMarker = createMapMarker(initialLatLng, newMap);

      setMap(newMap);
      setMarker(newMarker);

      if (initialLocation) {
        console.log("Initial location provided, performing reverse geocode");
        getReverseGeocode(initialLocation.lat, initialLocation.lng, onLocationSelected, false);
      }

    } catch (error) {
      console.error("Error initializing Google Maps:", error);
    }
  }, [initialLocation, isGoogleMapsLoaded, mapError, mapRef, onLocationSelected]);

  // Set up map event handlers
  useMapEvents({ map, marker, onLocationSelected });

  // Update marker position when initialLocation changes
  useEffect(() => {
    if (!map || !marker || !initialLocation) return;
    
    try {
      console.log("Updating marker position based on new initial location:", initialLocation);
      const newPosition = new google.maps.LatLng(initialLocation.lat, initialLocation.lng);
      marker.position = newPosition;
      
      try {
        map.setCenter(newPosition);
      } catch (centerError) {
        console.warn("Error centering map:", centerError);
      }
      
      console.log("Marker position updated based on initialLocation change");
    } catch (error) {
      console.error("Error updating marker position:", error);
    }
  }, [initialLocation, map, marker]);

  return { map, marker, isGoogleMapsLoaded, mapError };
};
